import { Token } from 'app/modules/Auth/types';
import axios, { Method } from 'axios';
import { TOKEN_KEY } from 'configs';
import { getFromLocal, removeFromLocal } from './cache';
import { auth } from 'app/modules/Auth/firebaseConfig';
import { saveTokenToLocal, startSession, endSession } from 'app/modules/Auth/services';
import { enLocale, LANGUAGE_STORAGE_KEY } from 'locales/i18n';
import { WARNING_SHOWN_KEY, LAST_ACTIVITY_KEY } from 'configs/constants';
import i18next from 'i18next';

interface IRequestArgs {
  url: string;
  method: Method;
  data?: any;
  params?: any;
  cancelToken?: any;
  signal?: AbortSignal;
  customHeaders?: any;
  headerVariant?: string;
}

let isCleaningUp = false; // Add flag to track cleanup state

const TOKEN_EXPIRY_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds

const getToken = async () => {
  const user = auth.currentUser;
  if (user) {
    try {
      // Get token result from Firebase (uses cache first)
      const tokenResult = await user.getIdTokenResult(false);

      // Check if token is expiring soon
      const expirationTime = new Date(tokenResult.expirationTime).getTime();

      if (Date.now() + TOKEN_EXPIRY_THRESHOLD >= expirationTime) {
        // Token is expiring soon, get a new one
        const newToken = await user.getIdToken(true);
        saveTokenToLocal({
          accessToken: newToken,
        });

        // Start new session with refreshed token
        try {
          await startSession();
        } catch (sessionError) {
          console.error('Failed to start new session:', sessionError);
        }

        return newToken;
      }

      // Get current stored token
      const storedToken = getFromLocal(TOKEN_KEY) as Token | null;

      // Check if stored token matches Firebase's cached token
      if (!storedToken?.accessToken || storedToken.accessToken !== tokenResult.token) {
        // Tokens don't match, update local storage with Firebase's token
        saveTokenToLocal({
          accessToken: tokenResult.token,
        });
        // session will be same as token is not changed based on refresh token
      }

      return tokenResult.token;
    } catch (error) {
      console.error('Error getting token from Firebase:', error);
      // On error, try stored token as fallback
      const storedToken = getFromLocal(TOKEN_KEY) as Token | null;
      return storedToken?.accessToken || '';
    }
  }

  // If no user, try stored token
  const storedToken = getFromLocal(TOKEN_KEY) as Token | null;
  return storedToken?.accessToken || '';
};

/**
 * @description Get authorization token
 * @return {String}
 */
export const onGetToken = async (): Promise<string | boolean> => {
  // Call function to get token
  const token = await getToken();
  if (token) {
    return token; // Second fallback from local storage state
  }
  return false;
};

/**
 * @description Get {Object} header based on the variant
 * @param {String} type
 * @define type: possible values 'authorization' || 'public'
 * @return {Object}
 */
export const onGetHeaders = async (type: string): Promise<any> => {
  const ALL_TYPES = ['authorization', 'public'];
  if (!type) {
    throw new Error('request func internal onGetHeaders arg @param type is missing');
  }
  if (!ALL_TYPES.includes(type)) {
    throw new Error(`
      request func internal onGetHeaders arg @param type can only be
      ${ALL_TYPES.join(',')}
    `);
  }

  const selectedLanguage = getFromLocal(LANGUAGE_STORAGE_KEY) || enLocale;
  const baseHeaders = {
    'Accept-Language': selectedLanguage,
  };

  if (type === 'authorization') {
    const authorizationToken = await onGetToken();
    return {
      ...baseHeaders,
      authorization: `Bearer ${authorizationToken}`,
    };
  }
  if (type === 'public') {
    return {
      ...baseHeaders,
      'Content-Type': 'application/json',
      headerVariant: 'public',
    };
  }
  return baseHeaders;
};

/**
 * @description Call an asynchronous XHR request
 * @param {String} url* required
 * @param {String} method* required
 * @param {Object} data optional
 * @param {Signal} cancelToken optional
 * @param {Object} customHeader optional
 * @param {String} headerVariant* required, by default 'authorization'
 * @return {Object} XHR response
 */
const request = async ({
  url,
  method,
  data,
  params,
  cancelToken,
  signal,
  customHeaders,
  headerVariant = 'authorization',
  ...rest
}: IRequestArgs) => {
  if (!url) {
    throw new Error('request func arg @param url is missing');
  }
  if (!method) {
    throw new Error('request func arg @param method is missing. Valid options "GET"|"POST"|"PUT" etc');
  }

  // Overrides headers object based on variant if "customHeaders" object provided
  const authHeaders = await onGetHeaders(headerVariant);
  const headers = { ...authHeaders, ...customHeaders };

  const result = await axios(url, {
    method,
    headers,
    ...(data && { data }),
    ...(params && { params }),
    ...(cancelToken && { cancelToken }),
    ...(signal && { signal }),
    ...rest,
  });
  return result.data;
};

interface IInterceptorsConfig {
  debug: boolean;
}

export const setupInterceptors = ({ debug = false }: IInterceptorsConfig) => {
  /**
   * @description echo() prints a message if allowed, of a certain type of console variant
   * @param {*} show {Boolean}
   * @param {*} type {String} Can be log, warn, debug
   * @param {*} message {String}
   */
  const echo = ({ show }: any) => {
    if (!show) {
      console.log('');
    }
    // console.group('🤯🤯 @@@AXIOS INTERCEPTOR@@@ 🤯🤯');
    // console[type](message);
    // console.groupEnd('@@@AXIOS INTERCEPTOR@@@');
  };

  // Request interceptor - Add auth header
  axios.interceptors.request.use(
    async (config) => {
      // Only add auth header if not a public route
      if (!config.headers.authorization && config.headers.headerVariant !== 'public') {
        try {
          // This will now check expiration and refresh if needed
          const token = await getToken();
          if (token) {
            config.headers.authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('Error in request interceptor:', error);
        }
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  // Response interceptor - Handle token refresh and network errors
  axios.interceptors.response.use(
    (response) => {
      echo({ show: debug, type: 'dir', message: response });
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Enhanced network error detection
      const isNetworkError =
        !navigator.onLine ||
        error.code === 'ECONNABORTED' ||
        error.code === 'ERR_NETWORK' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        error.message?.includes('Failed to fetch');

      if (isNetworkError) {
        // Network error detected - the network monitor will handle the notification
        console.warn('Network error detected:', error.message);
        return Promise.reject(error);
      }

      // Keep existing 401 handling as a fallback only
      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        originalRequest.headers.headerVariant !== 'public'
      ) {
        originalRequest._retry = true;

        try {
          // Force a new token as a last resort
          const user = auth.currentUser;
          if (!user) throw new Error('No authenticated user');
          const newToken = await user.getIdToken(true);
          saveTokenToLocal({
            accessToken: newToken,
          });

          originalRequest.headers.authorization = `Bearer ${newToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          await handleSessionCleanup();
        }
      }

      return Promise.reject(error);
    },
  );
};

// Helper function to handle session cleanup
export const handleSessionCleanup = async () => {
  // Prevent multiple simultaneous cleanup attempts
  if (isCleaningUp) {
    return;
  }

  isCleaningUp = true;
  try {
    // Attempt to end session on backend
    await endSession();
  } catch (e) {
    console.error('Failed to end session');
  } finally {
    // Always clean up local state
    removeFromLocal(TOKEN_KEY);
    removeFromLocal('firstLoginMessageShown');
    removeFromLocal(LAST_ACTIVITY_KEY);
    removeFromLocal(WARNING_SHOWN_KEY);

    // Sign out from Firebase
    await auth.signOut();

    // Reset cleanup flag
    isCleaningUp = false;
  }
};

export type ErrorMessage = { response: { data: { message: string; status: number; errorCode?: string } } };

export const getErrorMessage = (error: ErrorMessage) => {
  if (!error?.response?.data?.message) {
    return i18next.t('errors.common.somethingWentWrong');
  }

  return error?.response?.data?.message;
};

export const getErrorCodeAndStatus = (error: any) => {
  return {
    code: error.code,
    status: error.status,
  };
};

export default request;
